import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { getPlanningAuditLog } from '../../_services/planningAudit.service';
import {
    AUDIT_ACTION_LABELS,
    EMPTY_AUDIT_FILTERS,
    buildAuditQuery,
    formatAuditChange,
    formatAuditTime,
    formatAuditUser,
    getAuditDateRangeError,
    getAuditPageCount,
    hasAuditFilters,
} from './planningAuditLog';
import PlanningAuditFilters from './PlanningAuditFilters.jsx';
import './PlanningAuditLog.css';

export const AUDIT_PAGE_SIZE = 50;
export const JOB_NO_DEBOUNCE_MS = 400;
const POLL_MS = 30000;
const MAX_POLL_MS = 5 * 60 * 1000;
const SKELETON_ROWS = 5;

// Kept outside the component so reopening the tab shows the last rows at once
// while they are fetched again in the background (stale-while-revalidate).
const auditCache = new Map();

function cacheKey(query) {
    let accountId = '';
    try {
        accountId = (JSON.parse(localStorage.getItem('user') || '{}') || {}).accountId || '';
    } catch (_error) {
        accountId = '';
    }
    return `${accountId}|${JSON.stringify(query)}`;
}

export function clearPlanningAuditCache() {
    auditCache.clear();
}

export default function PlanningAuditLog() {
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState(EMPTY_AUDIT_FILTERS);
    const [jobNoFilter, setJobNoFilter] = useState('');
    const rangeError = getAuditDateRangeError(filters.from, filters.to);
    const query = useMemo(
        () => buildAuditQuery({ ...filters, jobNo: jobNoFilter }, page, AUDIT_PAGE_SIZE),
        [filters.from, filters.to, filters.action, jobNoFilter, page]
    );
    const key = cacheKey(query);
    // result.key tells which query the rows on screen belong to.
    const [result, setResult] = useState(() => {
        const cached = auditCache.get(key);
        return cached ? { key, body: cached } : null;
    });
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const controllerRef = useRef(null);
    const failuresRef = useRef(0);
    const latestRef = useRef({ key, query, rangeError });
    latestRef.current = { key, query, rangeError };

    const load = useCallback(async () => {
        const { key: requestKey, query: requestQuery, rangeError: invalid } = latestRef.current;
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = null;
        if (invalid) {
            setRefreshing(false);
            return;
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        setRefreshing(true);
        try {
            const body = await getPlanningAuditLog(requestQuery, { signal: controller.signal });
            auditCache.set(requestKey, body);
            failuresRef.current = 0;
            setResult({ key: requestKey, body });
            setError('');
        } catch (err) {
            if (err && err.name === 'AbortError') return;
            failuresRef.current += 1;
            setError((err && err.message) || 'Unable to load the audit log.');
        } finally {
            if (controllerRef.current === controller) {
                controllerRef.current = null;
                setRefreshing(false);
            }
        }
    }, []);

    // Wait for a pause in typing before searching by job no.
    useEffect(() => {
        const timer = window.setTimeout(() => setJobNoFilter(filters.jobNo), JOB_NO_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [filters.jobNo]);

    // Show cached rows for this query right away, then revalidate them.
    // Without a cache hit the previous rows stay on screen, dimmed, until
    // the new ones arrive.
    useEffect(() => {
        const cached = auditCache.get(key);
        if (cached) setResult({ key, body: cached });
        load();
    }, [key, load]);

    // Smart polling: only while the browser tab is visible, never on top of a
    // request in flight, refresh on return, and back off after failures.
    useEffect(() => {
        let cancelled = false;
        let timer = null;
        const schedule = () => {
            const delay = Math.min(POLL_MS * (2 ** failuresRef.current), MAX_POLL_MS);
            timer = window.setTimeout(tick, delay);
        };
        const tick = async () => {
            if (!document.hidden && !controllerRef.current) await load();
            if (!cancelled) schedule();
        };
        const onVisibilityChange = () => {
            if (!document.hidden && !controllerRef.current) load();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        schedule();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [load]);

    useEffect(() => () => {
        if (controllerRef.current) controllerRef.current.abort();
    }, []);

    const changeFilters = (changes) => {
        setFilters(current => ({ ...current, ...changes }));
        setPage(1);
    };
    const clearFilters = () => {
        setFilters(EMPTY_AUDIT_FILTERS);
        setJobNoFilter('');
        setPage(1);
    };

    const body = result ? result.body : null;
    const stale = Boolean(result) && result.key !== key;
    const rows = (body && Array.isArray(body.data)) ? body.data : [];
    const total = body ? Number(body.total) || 0 : 0;
    const pageCount = getAuditPageCount(total, AUDIT_PAGE_SIZE);
    const filtered = hasAuditFilters(filters);

    return (
        <section className="planning-audit" aria-busy={refreshing} aria-label="Planning audit log">
            <PlanningAuditFilters filters={filters} error={rangeError} onChange={changeFilters} onClear={clearFilters} />

            <div className="planning-audit__bar">
                <span className="planning-audit__summary">
                    {body ? `${total} ${total === 1 ? 'change' : 'changes'}${filtered ? ' found' : ''}` : 'Loading audit log…'}
                </span>
                <span className="planning-audit__refresh" role="status" aria-live="polite" aria-atomic="true">
                    {refreshing && body && <>
                        <Spinner animation="border" size="sm" aria-hidden="true" />
                        <span className="visually-hidden">Refreshing audit log…</span>
                    </>}
                </span>
                <Button variant="outline-secondary" size="sm" disabled={refreshing || Boolean(rangeError)} onClick={load}>
                    Refresh
                </Button>
            </div>

            {error && (
                <div className="planning-calendar__status planning-audit__error" role="alert">
                    {error}
                    <Button variant="link" size="sm" onClick={load}>Retry</Button>
                </div>
            )}

            <div className={`planning-audit__table-wrap${stale ? ' is-stale' : ''}`}>
                <table className="planning-audit__table">
                    <thead>
                        <tr>
                            <th scope="col">When</th>
                            <th scope="col">Action</th>
                            <th scope="col">Job No</th>
                            <th scope="col">Changes</th>
                            <th scope="col">By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!body && !error && Array.from({ length: SKELETON_ROWS }, (_item, index) => (
                            <tr key={`skeleton-${index}`} className="planning-audit__skeleton" aria-hidden="true">
                                <td colSpan={5}><span /></td>
                            </tr>
                        ))}
                        {body && rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="planning-audit__empty">
                                    {filtered ? 'No changes match these filters.' : 'No planning changes yet.'}
                                </td>
                            </tr>
                        )}
                        {rows.map(row => (
                            <tr key={row.id}>
                                <td className="planning-audit__time">{formatAuditTime(row.createdAt)}</td>
                                <td>
                                    <span className={`planning-audit__action is-${String(row.action || '').toLowerCase()}`}>
                                        {AUDIT_ACTION_LABELS[row.action] || row.action}
                                    </span>
                                </td>
                                <td>{row.jobNo || '—'}</td>
                                <td>
                                    <ul className="planning-audit__changes">
                                        {(row.changes || []).map((change, index) => (
                                            <li key={`${change.field}-${index}`}>{formatAuditChange(row.action, change)}</li>
                                        ))}
                                    </ul>
                                </td>
                                <td>
                                    {formatAuditUser(row)}
                                    {row.displayName && row.username && row.displayName !== row.username && (
                                        <div className="planning-audit__username">{row.username}</div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {pageCount > 1 && !stale && (
                <nav className="planning-audit__pager" aria-label="Audit log pages">
                    <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage(current => current - 1)}>
                        Previous
                    </Button>
                    <span>Page {page} of {pageCount}</span>
                    <Button variant="outline-secondary" size="sm" disabled={page >= pageCount} onClick={() => setPage(current => current + 1)}>
                        Next
                    </Button>
                </nav>
            )}
        </section>
    );
}
